import { PaymentType } from "@1games-ru/region-shared/dist/enums/payment/payment";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import EstateMpProvider from "@/core/data-provider/data-mp/estate/estate-mp-provider.ts";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import {
  estateSelector,
  feeSelectedDaysSelector,
  setEstateBalance,
  setFeeSelectedDays,
} from "@/domains/estate/slice";
import toast from "@/domains/toasts/helpers/toast";

export default () => {
  const { t } = useTranslation("estate");
  const [isPending, setIsPending] = useState(false);
  const estate = useAppSelector(estateSelector);
  const selectedDays = useAppSelector(feeSelectedDaysSelector);
  const dispatch = useDispatch();

  const setSelectedDays = useCallback(
    (days: number) => {
      dispatch(setFeeSelectedDays(days));
    },
    [dispatch],
  );

  const getFeeAmountBySelectedDays = useCallback(() => {
    if (!estate) return null;
    const maxPayAmount = estate.maxFee - estate.feeBalance;
    const payAmount = selectedDays * estate.totalFee;

    if (payAmount > maxPayAmount) {
      return maxPayAmount;
    }

    if (payAmount < 0) {
      return 0;
    }

    return payAmount;
  }, [estate?.totalFee, estate?.maxFee, estate?.feeBalance, selectedDays]);

  const updateEstateBalance = useCallback(async () => {
    if (estate) {
      const response = await EstateMpProvider.getDetails(estate.id);

      if (response instanceof RpcMpProviderFailedResponse) {
        toast.error(t(`estate.errors.${response.error.message}`));
        return;
      }
      dispatch(setEstateBalance(response.data.feeBalance));
    }
  }, [dispatch, estate?.id, t]);

  const payFeeHandler = useCallback(async () => {
    try {
      if (estate) {
        setIsPending(true);
        const amount = getFeeAmountBySelectedDays();
        const response = await EstateMpProvider.feePay(estate.id, amount, PaymentType.Card);
        if (response instanceof RpcMpProviderSuccessResponse) {
          setSelectedDays(0);
          await updateEstateBalance();
        } else {
          console.log("fee pay error:", response);
          toast.error(t(`estate.errors.${response.error.message}`));
        }
      }
    } catch (e) {
      console.log(e);
      toast.error(t("estate.errors.PAY_FEE_SYSTEM_ERROR"));
    } finally {
      setIsPending(false);
    }
  }, [getFeeAmountBySelectedDays, estate?.id, setSelectedDays, updateEstateBalance, t]);

  return {
    estate,
    payFeeHandler,
    isPending,
    selectedDays,
    setSelectedDays,
    getFeeAmountBySelectedDays,
  };
};
